import { CHANNELS } from "../shared/channels";
import { FeedItem } from "../shared/achievements";
import { call, listen } from "./bridge";
import { locale, t } from "./i18n";
import { canUse, myId } from "./me";

// the main feed's entries, fetched once and kept fresh by the live updates. the feed screen, the home tile and the widget all read this

let items: FeedItem[] = [];
let loaded: Promise<void> | null = null;
const watchers = new Set<(items: FeedItem[]) => void>();

function take(next: FeedItem[]): void {
  items = Array.isArray(next) ? next : [];
  watchers.forEach((watcher) => watcher(items));
}

export function feedItems(): FeedItem[] {
  return items;
}

export function loadFeed(force = false): Promise<FeedItem[]> {
  if (!canUse("board")) return Promise.resolve([]);
  if (!loaded || force) loaded = call<FeedItem[]>(CHANNELS.feedList).then(take).catch(() => { loaded = null; });
  return loaded.then(() => items);
}

export function onFeed(watcher: (items: FeedItem[]) => void): void {
  watchers.add(watcher);
}

let wired = false;
export function watchFeed(): void {
  if (wired) return;
  wired = true;
  listen(CHANNELS.feedUpdated, () => void loadFeed(true));
}

async function act(channel: string, ...args: unknown[]): Promise<void> {
  take(await call<FeedItem[]>(channel, ...args));
}

export async function postToFeed(text: string, image = ""): Promise<void> {
  await act(CHANNELS.feedPost, text, image);
  markSeen();
}

export const editPost = (id: string, text: string) => act(CHANNELS.feedEdit, id, text);
export const cheerPost = (id: string) => act(CHANNELS.feedCheer, id);
export const removePost = (id: string) => act(CHANNELS.feedRemove, id);
export const commentOn = (id: string, text: string) => act(CHANNELS.feedComment, id, text);
export const removeCommentOn = (id: string, commentId: string) => act(CHANNELS.feedCommentRemove, id, commentId);

// "now", "12m", "3h", "yesterday", then the day
export function whenLabel(at: string): string {
  const time = Date.parse(at);
  if (!time) return "";
  const minutes = Math.floor((Date.now() - time) / 60_000);
  if (minutes < 1) return t("now");
  if (minutes < 60) return t("{n}m", { n: minutes });
  if (minutes < 60 * 24 && new Date(time).getDate() === new Date().getDate()) return t("{n}h", { n: Math.floor(minutes / 60) });
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (new Date(time).toDateString() === yesterday.toDateString()) return t("yesterday");
  return new Date(time).toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

// what this screen last read, so entries from someone else after it light up on home
const seenKey = () => `chi.feed.seen.${myId() || "me"}`;

export function lastSeen(): string {
  try {
    return localStorage.getItem(seenKey()) || "";
  } catch {
    return "";
  }
}

export function markSeen(): void {
  try {
    localStorage.setItem(seenKey(), new Date().toISOString());
  } catch {
    // private mode, nothing lights up
  }
}

export function isNew(item: FeedItem, seen = lastSeen()): boolean {
  return Boolean(seen) && item.at > seen && !item.mine;
}
